import { type AxiosResponse } from 'axios'
import { type AxiosOptions } from 'shared/api'
import { APP_LOCALHOST, GOOGLE_RECAPTCHA_ENABLE } from 'shared/config'
import { jsonParse } from 'shared/lib/jsonParse.ts'
import {
  type IOptionsUpdateAuthRefresh,
  type IParamsSwitch,
  type IResponse2FaQrCode,
  type IResponse2FASettings,
  type IResponseSwitch,
} from 'entities/Auth'
import { baseApi } from 'entities/Auth/lib'
import { type IntegrationKey } from 'entities/Integrations'
import type {
  IParamsLogin,
  IResponseLogin,
  IResponseAuthRefresh,
  IResponseLogout,
  IParamsValidateEmail,
  IResponseValidateEmail,
  IResponseCountries,
  IParamsRegister,
  IResponseValidateRegister,
  IParamsRegisterAnswers,
  IResponseRegisterAnswers,
  IResponseRegister,
  IResponseSendTwoFaCode,
  IParamsSendTwoFaCode,
  IParamsVerifyTwoFaCode,
  IResponseVerifyTwoFaCode,
  IParamsForgotPassword,
  IResponseForgotPassword,
  IParamsResetPasswordToken,
  IResponseResetPassword,
  IParamsResetPassword,
  IResponseResetPasswordToken,
  IParamsMemberInviteToken,
  IResponseMemberInviteToken,
  IParamsAcceptMemberInvite,
  IResponseAcceptMemberInvite,
  IParamsAgencyInviteToken,
  IResponseAgencyInviteToken,
  IParamsValidateAgencyInvite,
  IResponseValidateAgencyInvite,
  IParamsAcceptAgencyInvite,
  IResponseAcceptAgencyInvite,
  IParamsSignUpRegister,
  IParamsValidateMaxMind,
  IResponseValidateMaxMind,
  IResponseSignUpRegister,
  IParamsSignUpOrganization,
} from './types'

class Api {
  login(data: IParamsLogin, options: AxiosOptions): Promise<AxiosResponse<IResponseLogin>> {
    return baseApi.post('2fa/auth/login', data, {
      apiVersion: APP_LOCALHOST || !GOOGLE_RECAPTCHA_ENABLE ? 'v5' : 'v6',
      ...options,
    })
  }

  logout(): Promise<AxiosResponse<IResponseLogout>> {
    return baseApi.post('auth/logout', null, { withCredentials: true })
  }

  updateAuthRefresh(
    options?: IOptionsUpdateAuthRefresh
  ): Promise<AxiosResponse<IResponseAuthRefresh>> {
    const assumeToken = sessionStorage.getItem('assume_token')
    const config = assumeToken
      ? undefined
      : {
          withCredentials: true,
        }

    const app_light_provider = jsonParse<{
      provider_name: IntegrationKey
      provider_id: number
    }>(sessionStorage.getItem('app_light_provider'))
    const body = app_light_provider
      ? {
          ...app_light_provider,
        }
      : null

    return baseApi.post('auth/refresh', body, {
      ...config,
      enabledChangeDomainApi: options?.enabledChangeDomainApi,
    })
  }

  switch(data: IParamsSwitch): Promise<AxiosResponse<IResponseSwitch>> {
    return baseApi.post('auth/switch', data)
  }

  validateEmail(data: IParamsValidateEmail): Promise<AxiosResponse<IResponseValidateEmail>> {
    return baseApi.post('validate/email', data)
  }

  validateMaxMind(
    data: IParamsValidateMaxMind,
    options: AxiosOptions
  ): Promise<AxiosResponse<IResponseValidateMaxMind>> {
    return baseApi.post('sign-up/validate', data, {
      apiVersion: APP_LOCALHOST || !GOOGLE_RECAPTCHA_ENABLE ? 'v5' : 'v6',
      ...options,
    })
  }

  signUpRegister(
    data: IParamsSignUpRegister,
    options: AxiosOptions
  ): Promise<AxiosResponse<IResponseSignUpRegister>> {
    return baseApi.post('sign-up/register', data, {
      apiVersion: APP_LOCALHOST || !GOOGLE_RECAPTCHA_ENABLE ? 'v5' : 'v6',
      ...options,
    })
  }

  signUpOrganization(
    data: IParamsSignUpOrganization,
    options: AxiosOptions
  ): Promise<AxiosResponse<IResponseSignUpRegister>> {
    return baseApi.post('sign-up/organization', data, {
      withCredentials: true,
      apiVersion: APP_LOCALHOST || !GOOGLE_RECAPTCHA_ENABLE ? 'v5' : 'v6',
      ...options,
    })
  }

  getCountries(): Promise<AxiosResponse<IResponseCountries>> {
    return baseApi.get('countries')
  }

  validateRegister(data: IParamsRegister): Promise<AxiosResponse<IResponseValidateRegister>> {
    return baseApi.post('users/register/validate', data)
  }

  register(data: IParamsRegister): Promise<AxiosResponse<IResponseRegister>> {
    return baseApi.post('auth/register', data, { withCredentials: true })
  }

  sendTwoFaCode(data: IParamsSendTwoFaCode): Promise<AxiosResponse<IResponseSendTwoFaCode>> {
    return baseApi.post('2fa/auth/send', data, {
      tokenType: '2fa',
    })
  }

  verifyTwoFaCode(data: IParamsVerifyTwoFaCode): Promise<AxiosResponse<IResponseVerifyTwoFaCode>> {
    return baseApi.post('2fa/auth/verify', data, {
      tokenType: '2fa',
    })
  }

  sendTwoFaCodeAuthorized(
    data: IParamsSendTwoFaCode
  ): Promise<AxiosResponse<IResponseSendTwoFaCode>> {
    return baseApi.post('2fa/send', data)
  }

  verifyTwoFaCodeAuthorized(
    data: IParamsVerifyTwoFaCode
  ): Promise<AxiosResponse<IResponseVerifyTwoFaCode>> {
    return baseApi.post('2fa/verify', data)
  }

  logRegisterAnswers(
    params: IParamsRegisterAnswers
  ): Promise<AxiosResponse<IResponseRegisterAnswers>> {
    return baseApi.post('profile/metadata', params)
  }

  forgotPassword(params: IParamsForgotPassword): Promise<AxiosResponse<IResponseForgotPassword>> {
    return baseApi.post('auth/forgot', params)
  }

  resetPassword(params: IParamsResetPassword): Promise<AxiosResponse<IResponseResetPassword>> {
    return baseApi.post('auth/reset', params)
  }

  validateResetPasswordToken(
    params: IParamsResetPasswordToken
  ): Promise<AxiosResponse<IResponseResetPasswordToken>> {
    return baseApi.post('auth/validate', params)
  }

  validateMemberInviteToken(
    params: IParamsMemberInviteToken
  ): Promise<AxiosResponse<IResponseMemberInviteToken>> {
    return baseApi.get(`organization/invites/${params.token}`)
  }

  acceptMemberInvite(
    params: IParamsAcceptMemberInvite
  ): Promise<AxiosResponse<IResponseAcceptMemberInvite>> {
    return baseApi.post('auth/register/accept', params)
  }

  validateAgencyInviteToken(
    params: IParamsAgencyInviteToken
  ): Promise<AxiosResponse<IResponseAgencyInviteToken>> {
    return baseApi.get(`auth/agency/${params.token}`)
  }

  validateAgencyInvite(
    params: IParamsValidateAgencyInvite
  ): Promise<AxiosResponse<IResponseValidateAgencyInvite>> {
    return baseApi.post('auth/agency/validate', params)
  }

  acceptAgencyInvite(
    params: IParamsAcceptAgencyInvite
  ): Promise<AxiosResponse<IResponseAcceptAgencyInvite>> {
    return baseApi.post('auth/agency', params)
  }

  getTwoFAuthSettings(): Promise<AxiosResponse<IResponse2FASettings>> {
    return baseApi.get('2fa')
  }

  getTwoFAuthQrCode(): Promise<AxiosResponse<IResponse2FaQrCode>> {
    return baseApi.get('2fa/qr-code')
  }
}

export const AuthApi = new Api()
